import { buildIsoWeekKey } from 'modules/shared/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from 'utils/logger';
import { MAX_WEEKLY_WORKING_HOURS } from '../../constants';
import { getEmployeeWeekSchedule } from '../../services';
import { buildAvailabilityMap } from '../utils';

/**
 * useEmployeeAvailability
 *
 * A hook for loading and caching employee availability by week.
 *
 * @param {number} employeeId
 * @returns {{
 *   loading: boolean,
 *   availabilityByWeek: Record<string, { assignedHours: number, remainingHours: number, isAvailable: boolean }>,
 *   getAvailabilityForWeek: (isoWeek: { year: number, week: number }) => { assignedHours: number, remainingHours: number, isAvailable: boolean } | null,
 *   invalidateEmployee: (id: number) => void,
 * }}
 */
export function useEmployeeAvailability(employeeId) {
  const cacheRef = useRef({});
  const [loading, setLoading] = useState(false);
  const [availabilityByWeek, setAvailabilityByWeek] = useState({});

  const invalidateEmployee = useCallback(
    (id) => {
      if (!id) return;

      delete cacheRef.current[id];

      if (id === employeeId) {
        setAvailabilityByWeek({});
      }
    },
    [employeeId],
  );

  useEffect(() => {
    setAvailabilityByWeek({});
  }, [employeeId]);

  useEffect(() => {
    if (!employeeId) return;

    if (cacheRef.current[employeeId]) {
      setAvailabilityByWeek(cacheRef.current[employeeId]);
      return;
    }

    let cancelled = false;

    const loadAvailability = async () => {
      setLoading(true);

      try {
        const assignments = await getEmployeeWeekSchedule(employeeId);
        if (cancelled) return;
        const availabilityMap = buildAvailabilityMap(assignments);

        cacheRef.current[employeeId] = availabilityMap;
        setAvailabilityByWeek(availabilityMap);
      } catch (error) {
        if (cancelled) return;

        toast.error(
          logError({
            message: 'Failed to load employee availability',
            error,
            code: 'EMPLOYEE_AVAILABILITY_LOAD_FAILED',
          }),
        );

        setAvailabilityByWeek({});
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAvailability();

    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  /**
   * getAvailabilityForWeek
   *
   * Gets the availability for a given ISO week.
   *
   * @param {number} isoWeek
   * @returns {{ assignedHours: number, remainingHours: number, isAvailable: boolean } | null}
   */
  const getAvailabilityForWeek = (isoWeek) => {
    if (!isoWeek || typeof isoWeek !== 'object') return null;
    const { year, week } = isoWeek;
    if (!year || !week) return null;

    const key = buildIsoWeekKey({ year, week });

    return (
      availabilityByWeek[key] ?? {
        assignedHours: 0,
        remainingHours: MAX_WEEKLY_WORKING_HOURS,
        isAvailable: true,
      }
    );
  };

  return {
    loading,
    availabilityByWeek,
    getAvailabilityForWeek,
    invalidateEmployee,
  };
}
